import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, CalendarClock, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { nativeNotify } from "@/lib/notify";

const POLL_MS = 60_000;
const STORE_KEY = "medaCallReminderFired";

const PIPELINE_ROUTE = {
  mbbs: "/admin",
  general: "/admin/leads",
  counseling: "/admin/counselings",
};

function loadFired() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveFired(map) {
  try {
    // prune entries older than 24h to keep it small
    const cutoff = Date.now() - 86_400_000;
    const pruned = Object.fromEntries(Object.entries(map).filter(([, t]) => t > cutoff));
    localStorage.setItem(STORE_KEY, JSON.stringify(pruned));
  } catch {
    /* ignore */
  }
}

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    /* ignore */
  }
}

export default function ScheduledCallReminder() {
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const firedRef = useRef(loadFired());

  const check = useCallback(async () => {
    let items = [];
    try {
      const { data } = await api.get("/admin/scheduled-calls/upcoming");
      items = data?.items || [];
    } catch {
      return;
    }
    const now = Date.now();
    const fired = firedRef.current;
    const toShow = [];
    for (const it of items) {
      const t = new Date(it.scheduled_call_at).getTime();
      if (Number.isNaN(t)) continue;
      const mins = (t - now) / 60_000;
      const base = `${it.lead_id}|${it.scheduled_call_at}`;
      let stage = null;
      if (mins <= 10 && mins > -2) stage = "final";
      else if (mins <= 50 && mins > 10) stage = "early";
      if (!stage) continue;
      const key = `${base}|${stage}`;
      if (fired[key]) continue;
      fired[key] = now;
      toShow.push({ ...it, stage, mins: Math.max(0, Math.round(mins)) });
    }
    if (toShow.length) {
      saveFired(fired);
      setQueue((q) => [...q, ...toShow]);
      beep();
      for (const it of toShow) {
        nativeNotify(
          it.stage === "final" ? `📞 Call starting soon — ${it.name}` : `📅 Upcoming call — ${it.name}`,
          {
            body: `${it.mins <= 0 ? "Due now" : `In about ${it.mins} min`}${it.note ? ` · ${it.note}` : ""}`,
            tag: `call-${it.lead_id}-${it.stage}`,
            onClick: () => {
              const route = PIPELINE_ROUTE[it.pipeline] || "/admin/leads";
              nav(`${route}?focus=${it.lead_id}`);
            },
          },
        );
      }
    }
  }, [nav]);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [check]);

  const current = queue[0] || null;
  const close = () => setQueue((q) => q.slice(1));
  const openLead = () => {
    if (!current) return;
    const route = PIPELINE_ROUTE[current.pipeline] || "/admin/leads";
    nav(`${route}?focus=${current.lead_id}`);
    close();
  };

  if (!current) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="w-[92vw] max-w-sm gap-0 overflow-hidden p-0" data-testid="call-reminder-popup">
        <div className={`flex items-center gap-2 px-5 py-3 text-white ${current.stage === "final" ? "bg-rose-600" : "bg-[#0A2540]"}`}>
          <CalendarClock className="h-4 w-4" />
          <span className="text-sm font-semibold">
            {current.stage === "final" ? "Call starting soon" : "Upcoming scheduled call"}
          </span>
        </div>
        <div className="px-5 py-4">
          <div className="text-lg font-semibold text-[#0A2540]" data-testid="call-reminder-name">{current.name}</div>
          <div className="mt-0.5 text-sm text-slate-500">{current.phone}</div>
          <div className="mt-2 text-sm font-medium text-[#1877F2]" data-testid="call-reminder-when">
            {current.mins <= 0 ? "Due now" : `In about ${current.mins} min`}
            {" · "}
            {new Date(current.scheduled_call_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </div>
          {current.note && (
            <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">{current.note}</div>
          )}
          <div className="mt-4 flex items-center gap-2">
            {current.phone && (
              <a href={`tel:${current.phone}`} className="flex-1" data-testid="call-reminder-call-now">
                <Button className="w-full gap-1.5 bg-[#25D366] text-white hover:bg-[#1aa851]">
                  <PhoneCall className="h-4 w-4" /> Call now
                </Button>
              </a>
            )}
            <Button variant="outline" onClick={openLead} className="flex-1" data-testid="call-reminder-open-lead">
              Open lead
            </Button>
            <Button variant="ghost" size="icon" onClick={close} data-testid="call-reminder-dismiss" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
